// components/AuthModal/AuthModal.jsx
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import styles from "./AuthModal.module.css";
import Logo from "../../assets/images/logo.svg";
import {
  login as loginApi,
  signup as signupApi,
  forgot as forgotApi,
  changePassword as changePasswordApi,
} from "../../api/auth";
import { toast } from "react-toastify";
import { useApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../api/endpoints";

import { useNavigate } from "react-router-dom";

export default function AuthModal() {
  const navigate = useNavigate();
  const { response: roleData } = useApi(API_ENDPOINTS.USER_ROLES);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    contact_no: "",
    referral: "",
    role_id: "1",
    agree_policy: false,
    agree_marketing: false,
    role_name: "Author",
  });
  const [loading, setLoading] = useState(false);
  const [changePasswordData, setChangePasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const { authModal, setAuthModal, login, signup } = useAuth();
  const { open, tab } = authModal;

  if (!open) return null;
  const handleSignupChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRoleChange = (e) => {
    const role_id = e.target.value;
    const role_name = e.target.options[e.target.selectedIndex].dataset.name;

    setFormData((prev) => ({
      ...prev,
      role_id,
      role_name,
    }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await loginApi({ email, password });
      const data = response.data;
      if (response?.status === 200) {
        toast.success(data.message, {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        const { role_name, ...rest } = data.user;

        const finalData = {
          ...rest,
          role: role_name.toLowerCase(),
        };

        login({
          userdata: finalData,
          token: data.token,
        });
        if (finalData?.role.toLowerCase() === "mentor") {
          navigate("/dashboard/mentor");
        } else if (finalData?.role.toLowerCase() === "author") {
          navigate("/dashboard/author");
        } else if (finalData?.role.toLowerCase() === "institution") {
          navigate("/dashboard/institution");
        } else {
          navigate("/dashboard/author");
        }
        setAuthModal({ open: false, tab: "login" });
      } else {
        toast.error("Invalid credentials. Please try again.");
      }
    } catch (error) {
      const message = error.response?.data?.message;
      toast.error(message);
    }
    setLoading(false);
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    if (!formData.agree_policy) {
      alert("You must agree to the Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      // ✅ Call API (wait for response)
      const response = await signupApi({
        name: formData.firstName + " " + formData.lastName,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.confirmPassword,
        contact_no: formData.contact_no,
        referral: formData.referral,
        role_id: formData.role_id,
        agree_policy: formData.agree_policy ? 1 : 0,
        agree_marketing: formData.agree_marketing ? 1 : 0,
        role_name: formData.role_name,
      });

      if (response?.status === 200 || response?.status === 201) {
        toast.success("Signup successful!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
          contact_no: "",
          referral: "",
          role_id: "1",
          agree_policy: false,
          agree_marketing: false,
          role_name: "Author",
        });
        const data = response.data;
        const { role_name, ...rest } = data.user;
        const finalData = {
          ...rest,
          role: role_name.toLowerCase(),
        };

        signup({
          userdata: finalData,
          token: data.token,
        });
        if (finalData?.role.toLowerCase() === "mentor") {
          navigate("/dashboard/mentor");
        } else if (finalData?.role.toLowerCase() === "author") {
          navigate("/dashboard/author");
        } else if (finalData?.role.toLowerCase() === "institution") {
          navigate("/dashboard/institution");
        } else {
          navigate("/dashboard/author");
        }
        setAuthModal({ open: false, tab: "signup" });
      } else {
        toast.error("Signup failed! Please try again.");
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          errors[key].forEach((message) => {
            toast.error(message);
          });
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await forgotApi({ email });
      const data = response.data;
      if (response.ok) {
        toast.success(
          data.message || "Password reset link sent to your email.",
          {
            position: "top-center",
          }
        );
        setAuthModal({ open: true, tab: "login" });
      } else {
        toast.error(data.message || "Failed to send reset link.");
      }
    } catch (error) {
      toast.error(error.response.data.message);
    }

    setLoading(false);
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (
      changePasswordData.new_password !== changePasswordData.confirm_password
    ) {
      toast.error("New passwords do not match!");
      setLoading(false);
      return;
    }

    try {
      const passwordData = {
        current_password: changePasswordData.current_password,
        password: changePasswordData.new_password,
        password_confirmation: changePasswordData.confirm_password,
      };
      const response = await changePasswordApi(passwordData);

      if (response?.status === 200) {
        toast.success(
          response.data.message || "Password changed successfully!"
        );
        setChangePasswordData({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });
        setAuthModal({ open: false, tab: "login" });
      } else {
        toast.error("Failed to change password.");
      }
    } catch (error) {
      const message =
        error.response?.data?.message || "Error changing password.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Close button */}
        <button
          className={styles.closeBtn}
          onClick={() => setAuthModal({ open: false, tab: "login" })}
        >
          ✕
        </button>
        {tab !== "forgot" && tab !== "changePassword" && (
          <>
            {/* Logo */}
            <img src={Logo} alt="Logo" className={styles.logo} />

            {/* Tabs */}
            <div className={styles.tabButtons}>
              <button
                className={`${styles.tabBtn} ${
                  tab === "login" ? styles.active : ""
                }`}
                onClick={() => setAuthModal({ open: true, tab: "login" })}
              >
                Login
              </button>
              <button
                className={`${styles.tabBtn} ${
                  tab === "signup" ? styles.active : ""
                }`}
                onClick={() => setAuthModal({ open: true, tab: "signup" })}
              >
                Signup
              </button>
            </div>
          </>
        )}

        {/* LOGIN FORM */}
        {tab === "login" && (
          <form onSubmit={handleLoginSubmit} className={styles.form}>
            <div className={styles.formRow}>
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className={styles.formRow}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={styles.submitBtn}
            >
              Login
            </button>
            <div className={styles.forgotLink}>
              <button
                onClick={() => setAuthModal({ open: true, tab: "forgot" })}
              >
                Forgot Password?
              </button>
            </div>
          </form>
        )}
        {/* Forgot FORM */}
        {tab === "forgot" && (
          <form
            onSubmit={handleForgotPasswordSubmit}
            className={`${styles.form} ${styles.forgotForm}`}
          >
            <h4>Forgot Password</h4>
            <p>
              Enter your email address and we’ll send you reset instructions.
            </p>
            <div className={styles.formRow}>
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={styles.submitBtn}
            >
              {loading ? "Sending..." : "Send Reset Link"}
            </button>

            <div className={styles.forgotLink}>
              <button
                onClick={() => setAuthModal({ open: true, tab: "login" })}
              >
                ← Back to Login
              </button>
            </div>
          </form>
        )}
        {/* CHANGE PASSWORD FORM */}
        {tab === "changePassword" && (
          <form
            onSubmit={handleChangePasswordSubmit}
            className={`${styles.form} ${styles.forgotForm}`}
          >
            <h4>Change Password</h4>
            <div className={styles.formRow}>
              <input
                type="password"
                placeholder="Current Password"
                value={changePasswordData.current_password}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    current_password: e.target.value,
                  })
                }
                required
              />
            </div>
            <div className={styles.formRow}>
              <input
                type="password"
                placeholder="New Password"
                value={changePasswordData.new_password}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    new_password: e.target.value,
                  })
                }
                required
              />
            </div>
            <div className={styles.formRow}>
              <input
                type="password"
                placeholder="Confirm New Password"
                value={changePasswordData.confirm_password}
                onChange={(e) =>
                  setChangePasswordData({
                    ...changePasswordData,
                    confirm_password: e.target.value,
                  })
                }
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={styles.submitBtn}
            >
              {loading ? "Updating..." : "Change Password"}
            </button>
          </form>
        )}
        {/* SIGNUP FORM */}
        {tab === "signup" && (
          <div className={styles.signupContainer}>
            <form onSubmit={handleSignupSubmit} className={styles.form}>
              <div className={styles.formRow}>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleSignupChange}
                  placeholder="First Name"
                  required
                />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleSignupChange}
                  placeholder="Last Name"
                  required
                />
              </div>
              <div className={styles.formRow}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleSignupChange}
                  placeholder="Email Address"
                  required
                />
                <input
                  type="tel"
                  name="contact_no"
                  value={formData.contact_no}
                  onChange={handleSignupChange}
                  placeholder="Contact Number"
                />
              </div>
              <div className={styles.formRow}>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleSignupChange}
                  placeholder="Password"
                  required
                />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleSignupChange}
                  placeholder="Confirm Password"
                  required
                />
              </div>
              <div className={styles.formRow}>
                <select
                  name="role_id"
                  value={formData.role_id}
                  onChange={handleRoleChange}
                >
                  {roleData?.roles?.map((item) => (
                    <option
                      key={item.id}
                      value={item.id}
                      data-name={item.label}
                    >
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formRow}>
                <select
                  name="referral"
                  value={formData.referral}
                  onChange={handleSignupChange}
                >
                  <option value="">How did you hear about us?</option>
                  <option value="google">Google</option>
                  <option value="friend">Friend</option>
                  <option value="social">Social Media</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  name="agree_policy"
                  checked={formData.agree_policy}
                  onChange={handleSignupChange}
                />
                I agree to the Privacy Policy
              </label>

              <label className={styles.checkbox}>
                <input
                  type="checkbox"
                  name="agree_marketing"
                  checked={formData.agree_marketing}
                  onChange={handleSignupChange}
                />
                Send me tips, articles, and offers
              </label>

              <button
                type="submit"
                disabled={
                  loading ||
                  !formData.firstName ||
                  !formData.lastName ||
                  !formData.email ||
                  !formData.password ||
                  !formData.confirmPassword ||
                  !formData.agree_policy ||
                  !formData.role_id
                }
                className={styles.submitBtn}
              >
                Create Account
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
